/***
 *
 *   PUBLIC EDD FORM (feature 004)
 *   The customer-facing Enhanced Due Diligence form, opened from the
 *   tokenized link emailed when an EDD case is raised. Token-only (no PIN):
 *   reads the verification id from the route and the token from the query
 *   string, loads the configured client EDD form, and submits the answers.
 *
 *   GET  /api/public/edd/:verification_id?token=...  -> { form_id, form_schema }
 *   POST /api/public/edd/:verification_id?token=...  (answers)  -> { status }
 *
 **********/

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Axios from "axios";
import { useParams } from "react-router-dom";
import { Card, Loader, Message } from "components/lib";
import { applyFormTheme, useFormBrandColor } from "components/survey/formTheme";
import { StylesManager, Model } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/defaultV2.min.css";

const Settings = require("settings.json");
const serverUrl = Settings[process.env.NODE_ENV].server_url;

StylesManager.applyTheme("defaultV2");

function buildSurveyModel(schema, theme) {
  if (!schema || typeof schema !== "object") return null;
  const model = new Model(schema);
  model.showCompletedPage = false;
  return applyFormTheme(model, theme);
}

function formatSubmittedAnswers(sender, uploadPrefix) {
  const output = {};
  Object.keys(sender?.data || {}).forEach((key) => {
    const question = sender.getQuestionByName(key);
    if (!question) return;

    if (question.getType() === "file" && Array.isArray(question.value)) {
      output[key] = question.value.map((fileItem) => ({
        ...fileItem,
        key: fileItem.key || `${uploadPrefix}/${key}/${fileItem.name}`,
      }));
      return;
    }

    output[key] = question.value;
  });

  return output;
}

export function PublicEddView() {
  const params = useParams();
  const queryToken = useMemo(
    () => new URLSearchParams(window.location.search).get("token"),
    []
  );

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [formSchema, setFormSchema] = useState(null);
  // `undefined`, not null, is deliberate: useFormBrandColor reads a null theme as
  // "payload resolved, no theme" and would publish it over the frame colour View
  // seeded from the session cache, repainting the default gradient for the whole
  // fetch. The load path below always resolves this to a theme or null.
  const [formTheme, setFormTheme] = useState();

  // frames the page in the form's own colour (R8)
  useFormBrandColor(formTheme);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    async function loadForm() {
      if (!queryToken) {
        setError("This Enhanced Due Diligence link is missing its token.");
        setLoading(false);
        return;
      }

      try {
        const response = await Axios.get(`/api/public/edd/${params.id}`, {
          params: { token: queryToken },
        });
        if (!mounted) return;
        setFormSchema(response.data?.data?.form_schema || null);
        setFormTheme(response.data?.data?.theme || null);
      } catch (err) {
        if (!mounted) return;
        setError(
          err?.response?.data?.message ||
            "This Enhanced Due Diligence link is invalid or has expired."
        );
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadForm();
    return () => {
      mounted = false;
    };
  }, [params.id, queryToken]);

  const surveyModel = useMemo(
    () => buildSurveyModel(formSchema, formTheme),
    [formSchema, formTheme]
  );

  const uploadFiles = useCallback(
    (sender, options) => {
      const formData = new FormData();
      options.files.forEach((file) => {
        formData.append("file", file);
        formData.append("response_id", params.id);
        formData.append("field_name", options.name);
        formData.append("file_name", file.name);
      });

      Axios.post(`${serverUrl}/api/utility/gcp/upload`, formData)
        .then((response) => {
          options.callback(
            "success",
            options.files.map((file) => ({
              file,
              name: file.name,
              type: file.type || "application/octet-stream",
              content: response.data.url,
              key: response.data.name,
              id: file.id,
            }))
          );
        })
        .catch(() => {
          options.callback("error", "File upload failed");
        });
    },
    [params.id]
  );

  useEffect(() => {
    if (!surveyModel) return undefined;

    const completing = async (sender, options) => {
      if (isSubmittingRef.current) return;

      isSubmittingRef.current = true;
      options.allowComplete = false;
      setSubmitting(true);
      setError(null);

      try {
        const answers = formatSubmittedAnswers(sender, params.id);
        await Axios.post(`/api/public/edd/${params.id}`, answers, {
          params: { token: queryToken },
        });
        setCompleted(true);
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            "Unable to submit this form right now. Please try again."
        );
        isSubmittingRef.current = false;
      } finally {
        setSubmitting(false);
      }
    };

    surveyModel.onUploadFiles.add(uploadFiles);
    surveyModel.onCompleting.add(completing);

    return () => {
      surveyModel.onUploadFiles.remove(uploadFiles);
      surveyModel.onCompleting.remove(completing);
    };
  }, [surveyModel, uploadFiles, params.id, queryToken]);

  if (loading || submitting) return <Loader />;

  return (
    <Card title="Enhanced Due Diligence">
      {error && <Message type="warning">{error}</Message>}

      {completed && (
        <Message type="success">
          Thank you — your information has been submitted. Our team will be in
          touch if anything further is required.
        </Message>
      )}

      {!completed && !error && (
        <p className="pb-4 text-sm text-slate-700">
          Please complete the form below so we can finish our review.
        </p>
      )}

      {!completed && surveyModel && (
        <div className="overflow-x-auto">
          <Survey model={surveyModel} />
        </div>
      )}

      {!completed && !surveyModel && !error && (
        <Message type="warning">
          This Enhanced Due Diligence form is not available right now.
        </Message>
      )}
    </Card>
  );
}
